import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { OktaAuthStateService } from '@okta/okta-angular';
import { ADPResourceService, CustomFormControl, Resource } from 'digital-core';
import { pairwise } from 'rxjs';
import { AccountService } from 'src/app/core/services/account.service';
import { GlobalModalWindowService } from 'src/app/core/services/global-modal-window.service';
import { GoogleAnalyticsService } from 'src/app/core/services/google-tag-manager.service';
import { PGHOktaService } from 'src/app/core/services/pghokta.service';
import { SearchContractRequest, SearchParameter } from 'src/app/model/account/user-otp-request';
import { GTMOnboardingEvents, OnboardingSteps } from 'src/app/model/google-tag-manager/api-status';

@Component({
  selector: 'app-lookup-user',
  templateUrl: './lookup-user.component.html',
  styleUrls: ['./lookup-user.component.scss'],
})
export class LookupUserComponent implements OnInit, AfterViewInit {
  @Output() lookupUserResponse = new EventEmitter();
  @ViewChild('option1TabTemplate') option1TabTemplate!: TemplateRef<any>;
  @ViewChild('option2TabTemplate') option2TabTemplate!: TemplateRef<any>;

  isAuthenticated = false;
  syntheticToken = '';
  reCaptchaInvisbleKey = '';
  isOption1Visible = false;
  validationError: string;
  option2ValidationError: string;
  option1ValidationError: string;
  optionOrder = [];
  matTabList = [];
  searchByOption1Fg = new FormGroup({
    SearchTerm1: new CustomFormControl(),
  });
  searchByOption2Fg = new FormGroup({
    SearchTerm1: new CustomFormControl(),
  });
  option1SearchTerm1ResourceData: Resource;
  option2SearchTerm1ResourceData: Resource;
  showBothOptions = false;

  constructor(
    private oktaAuth: OktaAuthStateService,
    private router: Router,
    private route: ActivatedRoute,
    private pghOktaService: PGHOktaService,
    private accountService: AccountService,
    private globalModalWindowService: GlobalModalWindowService,
    private resourceService: ADPResourceService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private cdref: ChangeDetectorRef
  ) {
    this.oktaAuth.authState$.subscribe((authState) => (this.isAuthenticated = authState.isAuthenticated));
    this.route.queryParams.subscribe((params) => {
      if (params['st']) {
        this.syntheticToken = params['st'];
      }
    });
  }

  get isOptionNotValid(): boolean {
    if (this.isOption1Visible) {
      return this.checkOption(this.searchByOption1Fg, this.option1SearchTerm1ResourceData, 'SearchTerm1');
    } else {
      return this.checkOption(this.searchByOption2Fg, this.option2SearchTerm1ResourceData, 'SearchTerm1');
    }
  }

  ngAfterViewInit() {
    this.optionOrder.forEach((o) => {
      let otemplate = o == 1 ? this.option1TabTemplate : this.option2TabTemplate;
      this.matTabList.push({
        label: `LookupUser_Tab_Option${o}_Label`,
        template: otemplate,
      });
    });
    this.cdref.detectChanges();
  }

  ngOnInit() {
    this.isAuthenticated = this.pghOktaService.isTokenValid();
    if (this.isAuthenticated) {
      this.router.navigate(['/']);
    } else {
      this.optionOrder = JSON.parse(this.resourceService.getPortalSetting('LookupUser_Option_List_Order').Value);
      this.isOption1Visible = this.optionOrder[0] == 1;
      this.reCaptchaInvisbleKey = this.resourceService.getPortalSetting('Global_ReCaptcha2Invisible').Value;
      this.showBothOptions = this.resourceService.isPortalSettingTrue('Global_SPOE_Enabled');
      this.option1SearchTerm1ResourceData = this.resourceService.getResourceObject('LookupUser_Field_Option1_SearchTerm1');
      this.option2SearchTerm1ResourceData = this.resourceService.getResourceObject('LookupUser_Field_Option2_SearchTerm1');
      this.setFormValidators();
    }
  }

  recaptchaExecute(recaptcha) {
    // NOTE: This is to verify error as the button is always enabled
    if (this.showBothOptions) {
      this.option1ValidationError || this.validateInputFieldRequired(this.searchByOption1Fg, this.option1SearchTerm1ResourceData, 'SearchTerm1');
      this.option1ValidationError || this.validateInputFieldFormat(this.searchByOption1Fg, this.option1SearchTerm1ResourceData, 'SearchTerm1');
      this.option2ValidationError || this.validateInputFieldRequired(this.searchByOption2Fg, this.option2SearchTerm1ResourceData, 'SearchTerm1');
      this.option2ValidationError || this.validateInputFieldFormat(this.searchByOption2Fg, this.option2SearchTerm1ResourceData, 'SearchTerm1');
    } else if (this.isOption1Visible) {
      this.validationError || this.validateInputFieldRequired(this.searchByOption1Fg, this.option1SearchTerm1ResourceData, 'SearchTerm1');
      this.validationError || this.validateInputFieldFormat(this.searchByOption1Fg, this.option1SearchTerm1ResourceData, 'SearchTerm1');
    } else {
      this.validationError || this.validateInputFieldRequired(this.searchByOption2Fg, this.option2SearchTerm1ResourceData, 'SearchTerm1');
      this.validationError || this.validateInputFieldFormat(this.searchByOption2Fg, this.option2SearchTerm1ResourceData, 'SearchTerm1');
    }
    if (this.validationError || this.option1ValidationError || this.option2ValidationError) {
      return;
    }
    // NOTE : Disable recaptcha to enable testing
    let isRecpatchEnabled = this.resourceService.isPortalSettingTrue('Global_Recaptcha_Feature');
    if (!isRecpatchEnabled || this.syntheticToken !== '') {
      this.findContractByOption(this.syntheticToken, recaptcha);
    } else {
      recaptcha.execute();
    }
  }

  findContractByOption(token, recaptcha) {
    if (this.showBothOptions) {
      this.findContractByBothOptions(token, recaptcha);
    } else if (this.isOption1Visible) {
      this.findContractByOption1(token, recaptcha);
    } else {
      this.findContractByOption2(token, recaptcha);
    }
  }

  findContractByBothOptions(token, reCaptcha) {
    let field1Value = this.searchByOption1Fg.get('SearchTerm1').value;
    let resourceField1 = this.resourceService.getResourceObject('LookupUser_Field_Option1_SearchTerm1');
    let searchParameter1 = new SearchParameter(resourceField1.Value, field1Value);
    let field2Value = this.searchByOption2Fg.get('SearchTerm1').value;
    let resourceField2 = this.resourceService.getResourceObject('LookupUser_Field_Option2_SearchTerm1');
    let searchParameter2 = new SearchParameter(resourceField2.Value, field2Value);

    let searchRequest = new SearchContractRequest();
    searchRequest.reCaptchaValue = token;
    searchRequest.searchParameters = [];
    searchRequest.searchParameters.push(searchParameter1);
    searchRequest.searchParameters.push(searchParameter2);

    this.accountLookup(searchRequest, undefined, reCaptcha);
  }

  findContractByOption1(token, recaptcha) {
    if (this.searchByOption1Fg.valid) {
      this.findContract(this.searchByOption1Fg, 'LookupUser_Field_Option1_SearchTerm1', 'SearchTerm1', token, recaptcha);
    }
  }

  findContractByOption2(token, recaptcha) {
    if (this.searchByOption2Fg.valid) {
      this.findContract(this.searchByOption2Fg, 'LookupUser_Field_Option2_SearchTerm1', 'SearchTerm1', token, recaptcha);
    }
  }

  findContract(searchFormGroup, searchTerm1, field1, token, reCaptcha) {
    let field1Value = searchFormGroup.get(field1).value;
    let resourceField1 = this.resourceService.getResourceObject(searchTerm1);
    let searchParameter1 = new SearchParameter(resourceField1.Value, field1Value);

    let searchRequest = new SearchContractRequest();
    searchRequest.reCaptchaValue = token;
    searchRequest.searchParameters = [];
    searchRequest.searchParameters.push(searchParameter1);

    this.accountLookup(searchRequest, searchParameter1, reCaptcha);
  }

  accountLookup(searchRequest: SearchContractRequest, searchParameter: SearchParameter, reCaptcha: any) {
    let errorResp = undefined;
    this.accountService.accountLookup(searchRequest).subscribe({
      next: (response: any) => {
        if (response?.emails?.length || response?.phoneNumbers?.length) {
          this.lookupUserResponse.emit(response);
          errorResp = undefined;
          this.googleAnalyticsService.emitOnboardingEvent(GTMOnboardingEvents.Start, OnboardingSteps.Start);
        } else if (response?.errorCode) {
          errorResp = this.resourceService.getViewLabel(`Global_Error_Code_` + response.errorCode);
        } else {
          errorResp = this.resourceService.getViewLabel('Global_Modal_Error_Title');
        }
        this.validationError = errorResp;
      },
      error: (err) => {
        reCaptcha.reset();
        if (err.error && (err.error.ErrorCode === 'NoContractForEmail' || err.error.Message === 'Search error')) {
          if (this.showBothOptions) {
            errorResp = this.resourceService.getViewLabel('LookupUser_NotFound_Error');
          } else {
            errorResp = searchParameter.key === 'Email'
              ? this.resourceService.getViewLabel('LookupUser_NotFound_Error_Email')
              : this.resourceService.getViewLabel('LookupUser_NotFound_Error_Phone');
          }
        } else if (err.error && err.error.ErrorCode === 'OktaUserAlreadyExist') {
          errorResp = this.resourceService.getViewLabel('LookupUser_AlreadyExists_Error');
        } else if (err.error && err.error.ExceptionType == 'BadRequest') {
          errorResp = this.resourceService.getViewLabel('LookupUser_NotFound_Error');
        } else {
          throw err;
        }
        this.validationError = errorResp;
        this.googleAnalyticsService.emitErrorEvent(errorResp, 'BadRequest', '400');
      },
    });
  }

  goToLogin() {
    // TODO: investigate why the login form doesn't load when the user navigates back to the login screen
    const loginRoute = this.resourceService.getPortalSetting('URL_Login').Value;
    window.location.replace(loginRoute);
  }

  checkOption(formGroup: FormGroup, resource: Resource, searchTerm: string): boolean {
    const optionValue = formGroup.get(searchTerm).value;
    if (resource.Value?.toLowerCase() == 'phonenumber') {
      return !optionValue;
    } else if (resource.Value?.toLowerCase() == 'email') {
      return !optionValue || !String(optionValue).toLowerCase().match(resource.ValidationRegex);
    }
  }

  validateOptionSearchTerm1(formGroup: FormGroup, resource: Resource) {
    if ((formGroup.get('SearchTerm1').touched && !formGroup.get('SearchTerm1').dirty) || !formGroup.get('SearchTerm1').value) {
      this.validateInputFieldRequired(formGroup, resource, 'SearchTerm1');
    } else {
      this.validateInputFieldFormat(formGroup, resource, 'SearchTerm1');
    }
  }

  validateInputFieldRequired(formGroup: FormGroup, resource: Resource, searchTerm: string) {
    if (resource.Value?.toLowerCase() == 'email') {
      const error = !formGroup.get(searchTerm).value
        ? this.resourceService.getViewLabel('Global_Error_Required_Email')
        : undefined;
      if (this.showBothOptions) {
        this.option2ValidationError = error;
        this.validationError = undefined;
      } else {
        this.validationError = error;
      }
    } else if (resource.Value?.toLowerCase() == 'phonenumber') {
      const error = !formGroup.get(searchTerm).value
        ? this.resourceService.getViewLabel('Global_Error_Required_Phone_No')
        : undefined;
      if (this.showBothOptions) {
        this.option1ValidationError = error;
        this.validationError = undefined;
      } else {
        this.validationError = error;
      }
    }
  }

  validateInputFieldFormat(formGroup: FormGroup, resource: Resource, searchTerm: string) {
    const optionValue = formGroup.get(searchTerm).value;
    let isValid = true;
    if (resource.Value?.toLowerCase() == 'email') {
      isValid = !!String(optionValue).toLowerCase().match(resource.ValidationRegex);
      const error = !isValid ? this.resourceService.getViewLabel('Global_Error_Valid_Email') : undefined;
      if (this.showBothOptions) {
        this.option2ValidationError = error;
      } else {
        this.validationError = error;
      }
    } else if (resource.Value?.toLowerCase() == 'phonenumber') {
      isValid = !!(String(optionValue).length == 10);
      const error = !isValid ? this.resourceService.getViewLabel('Global_Error_Valid_Phone_No') : undefined;
      if (this.showBothOptions) {
        this.option1ValidationError = error;
      } else {
        this.validationError = error;
      }
    }
  }

  setFormValidators() {
    this.searchByOption1Fg.get('SearchTerm1').clearValidators();
    this.searchByOption2Fg.get('SearchTerm1').clearValidators();
    this.searchByOption1Fg.reset();
    this.searchByOption2Fg.reset();
    this.validationError = undefined;
    this.option1ValidationError = undefined;
    this.option2ValidationError = undefined;
    if (this.isOption1Visible) {
      this.searchByOption1Fg.get('SearchTerm1').addValidators(Validators.required);
      this.searchByOption1Fg
        .get('SearchTerm1')
        .valueChanges.pipe(pairwise())
        .subscribe(() => this.validateInputFieldRequired(this.searchByOption1Fg, this.option1SearchTerm1ResourceData, 'SearchTerm1'));
    } else {
      this.searchByOption2Fg.get('SearchTerm1').addValidators(Validators.required);
      this.searchByOption2Fg
        .get('SearchTerm1')
        .valueChanges.pipe(pairwise())
        .subscribe(() => this.validateInputFieldRequired(this.searchByOption2Fg, this.option2SearchTerm1ResourceData, 'SearchTerm1'));
    }
  }

  showOtherOption() {
    this.isOption1Visible = !this.isOption1Visible;
    this.setFormValidators();
  }

  showAccountInfo() {
    if (this.showBothOptions) {
      this.globalModalWindowService.openSPOESimpleErrorDialog();
    } else {
      this.globalModalWindowService.displaySimpleErrorModal(
        'LookupUser_Forgot_Account_Info_Description',
        'LookupUser_Forgot_Account_Info_Link',
      );
    }
  }

  getResourceViewLabel(item: string) {
    const label = this.resourceService.getViewLabel(item) ?? '';
    return this.resourceService.transformContentData(label);
  }
}
